/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import {
  Text,
  Box,
  Table,
  theme,
  Message,
  FlexContainer,
} from "@nordcloud/gnui";
import { CloudWasteCategoryKey } from "~/generated/graphql";
import { BarChart, UniversalWrap } from "~/components";
import { BrickLoader } from "~/components/Loaders/BrickLoader";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { formatMoney, isNotEmpty } from "~/tools";
import { COLORS } from "~/views/home/components/TopSpendingApps/constants";
import { CloudWasteWidgetCell } from "./CloudWasteWidgetCell";
import { useWasteSummary } from "./useWasteSummary";

export function CloudWasteWidget() {
  const [riSavings, setRISavings] = useState(0);
  const [azureSavings, setAzureSavings] = useState(0);
  const { currency } = useCurrency();

  const {
    loading,
    error,
    summaryData,
    wasteSummary,
    barsValues,
    azureReservations,
  } = useWasteSummary(riSavings, azureSavings);

  const total = wasteSummary
    .map((a) => a?.cost ?? 0)
    .reduce((a, b) => a + b, 0);

  const totalYearly = total * 12;

  useEffect(() => {
    setRISavings(
      Number(
        summaryData?.reservationsAndCommitmentsSavings?.totalMonthlySavings ?? 0
      )
    );
    setAzureSavings(azureReservations);
  }, [summaryData, azureReservations]);

  return (
    <Box minHeight="100%">
      <Box pb={theme.spacing.spacing04} px={0} pt={0}>
        <FlexContainer justifyContent="space-between">
          <Text isTitle tag="div" weight="medium">
            Savings Suggestions
          </Text>
          <Text tag="div" size="sm" data-testid="savings-suggestions__links">
            <Link
              css={{ marginRight: theme.spacing.spacing04 }}
              to={ROUTES.settings.cloudWaste}
            >
              Settings
            </Link>
            <Link to={`${ROUTES.estateRecords.index}?page=0&waste=true`}>
              See all
            </Link>
          </Text>
        </FlexContainer>
      </Box>
      <div>
        <UniversalWrap
          errorProps={{ error }}
          loaderProps={{
            loading,
            Component: BrickLoader,
            viewBox: "0 0 2 1",
          }}
        >
          <If condition={isNotEmpty(wasteSummary) && !error}>
            <Then>
              <FlexContainer columnGap="6rem" wrap="wrap">
                <div>
                  <Text
                    size="sm"
                    color={theme.color.text.text02}
                    mb={theme.spacing.spacing01}
                  >
                    This Month
                  </Text>
                  <Text size="lg" color={theme.color.text.success}>
                    {formatMoney(total, currency)}
                  </Text>
                </div>
                <div>
                  <Text
                    size="sm"
                    color={theme.color.text.text02}
                    mb={theme.spacing.spacing01}
                  >
                    Next 12 Months Savings
                  </Text>
                  <Text size="lg">{formatMoney(totalYearly, currency)}</Text>
                </div>
              </FlexContainer>
              <BarChart values={barsValues} />
              <Table small tiny>
                <Table.thead>
                  <Table.tr>
                    <Table.th>Savings Suggestions Groups</Table.th>
                    <Table.th style={{ textAlign: "right" }}>
                      Monthly Savings
                    </Table.th>
                    <Table.th style={{ textAlign: "right" }}>
                      12 Months Savings
                    </Table.th>
                  </Table.tr>
                </Table.thead>
                <Table.tbody>
                  {wasteSummary.map((cell, index) => (
                    <CloudWasteWidgetCell
                      key={cell?.category ?? ""}
                      color={COLORS[index]}
                      category={cell?.category as CloudWasteCategoryKey}
                      cloudWaste={`${cell?.cost}` ?? "0"}
                      cloudWasteYearly={`${cell?.costYearly}` ?? "0"}
                      estateRecords={cell?.estateRecords}
                      policies={cell?.policies ?? []}
                      uri={cell?.uri}
                      isVisible={cell?.isVisible}
                    />
                  ))}
                </Table.tbody>
              </Table>
            </Then>
            <Else>
              <Message status="notification" image="info">
                <Text size="md" tag="div" color={theme.color.text.info}>
                  No savings suggestions policies available in IBM Multicloud
                  Accelerator. Please set up policies in{" "}
                  <Link
                    to={ROUTES.settings.cloudWaste}
                    css={{
                      color: theme.color.text.text04,
                      textDecoration: "underline",
                    }}
                  >
                    Savings Suggestions Settings
                  </Link>
                </Text>
              </Message>
            </Else>
          </If>
        </UniversalWrap>
      </div>
    </Box>
  );
}
